/**
 * The template's PDP details settings (`app.config.ts` → `productDetails`):
 * - `specificationCodes` — the parametric codes the specification tab is
 *   limited to, or `undefined` for no limit;
 * - `hiddenSpecifications` — names or codes the specification tab leaves out
 *   unless the product page layout lists its own;
 * - `unlabelledSpecifications` — names whose row shows the value only, across
 *   the full width (the tab already says what it is);
 * - `hasDeliveryAndReturnsTab` — whether the `SubDescription` tab shows (on
 *   unless a template turns it off);
 * - `lowestPriceBeforeReduction` — the lowest-price note only on a reduction,
 *   naming the price before it (Omnibus) rather than the 30-day low;
 * - `lowestPriceAlways` — the note on every product: the 30-day low (today's
 *   price when it has not changed) wherever there is no before-reduction figure;
 * - `packThumbSize` — the multi pack picker's thumbnail size in CSS px, or
 *   `undefined` for its own;
 * - `showRating` — the Lipscore rating and a reviews link beside the price;
 * - `categoryBreadcrumb` — breadcrumbs from the product's primary category;
 * - `showStoreAvailability` — the "see availability in stores" link (on
 *   unless a template turns it off);
 * - `highlightCount` — how many `SubDescription` bullets the summary under
 *   the price shows, with the sub header and part number; `0` keeps the short
 *   description instead.
 * Cast on purpose, as in `useProductListingPagination`: vue-tsc infers the
 * layered app config as `unknown` here.
 */
export const useProductDetailsConfig = (): {
	specificationCodes: readonly string[] | undefined;
	hiddenSpecifications: readonly string[];
	unlabelledSpecifications: readonly string[];
	hasDeliveryAndReturnsTab: boolean;
	lowestPriceBeforeReduction: boolean;
	lowestPriceAlways: boolean;
	packThumbSize: number | undefined;
	showRating: boolean;
	categoryBreadcrumb: boolean;
	showStoreAvailability: boolean;
	highlightCount: number;
} => {
	const { productDetails } = useAppConfig() as {
		productDetails?: {
			specificationCodes?: readonly string[] | null;
			hiddenSpecifications?: readonly string[] | null;
			unlabelledSpecifications?: readonly string[] | null;
			deliveryAndReturnsTab?: boolean;
			lowestPriceBeforeReduction?: boolean;
			lowestPriceAlways?: boolean;
			packThumbSize?: number | null;
			rating?: boolean;
			categoryBreadcrumb?: boolean;
			storeAvailabilityLink?: boolean;
			highlights?: number | null;
		};
	};
	const codes = productDetails?.specificationCodes;
	const hidden = productDetails?.hiddenSpecifications;
	const unlabelled = productDetails?.unlabelledSpecifications;
	return {
		specificationCodes: Array.isArray(codes) ? codes : undefined,
		hiddenSpecifications: Array.isArray(hidden) ? hidden : [],
		unlabelledSpecifications: Array.isArray(unlabelled) ? unlabelled : [],
		hasDeliveryAndReturnsTab: productDetails?.deliveryAndReturnsTab !== false,
		lowestPriceBeforeReduction:
			productDetails?.lowestPriceBeforeReduction === true,
		lowestPriceAlways: productDetails?.lowestPriceAlways === true,
		packThumbSize:
			typeof productDetails?.packThumbSize === "number" &&
			productDetails.packThumbSize > 0
				? productDetails.packThumbSize
				: undefined,
		showRating: productDetails?.rating === true,
		categoryBreadcrumb: productDetails?.categoryBreadcrumb === true,
		showStoreAvailability: productDetails?.storeAvailabilityLink !== false,
		highlightCount:
			typeof productDetails?.highlights === "number" &&
			productDetails.highlights > 0
				? productDetails.highlights
				: 0
	};
};
